import React, { useEffect, useState } from "react";
import { Mail, Send } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Btn } from "./ui-kit";

/**
 * Multi-select recipient picker used by the "Email link" button on a proof.
 *
 * Props:
 *  - contacts: [{name, email}]    — the client's contacts list (pre-fetched)
 *  - onSend(selected[])           — called with the chosen recipients
 *  - triggerTestId                — data-testid for the trigger button
 *  - listTestIdPrefix             — prefix for per-recipient testids
 *
 * Behaviour:
 *  - Defaults every contact to TICKED so a single click on "Open draft"
 *    sends to everyone — that's by far the common case.
 *  - "Other recipient…" still works (empty recipient list → mail client
 *    opens with an empty To: line so the user can type anyone).
 */
const RecipientMultiSelectPopover = ({ contacts, onSend, onOpen, triggerTestId, listTestIdPrefix }) => {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(() => new Set(contacts.map((c) => c.email)));

  // Re-seed the selection every time the popover opens so a contact added /
  // removed under us is reflected, and so the user always starts with the
  // "send to everyone" default. Also fire onOpen so callers can lazy-fetch.
  useEffect(() => {
    if (open) {
      setPicked(new Set(contacts.map((c) => c.email)));
      if (onOpen) onOpen();
    }
  }, [open, contacts, onOpen]);

  const togglePick = (email) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email); else next.add(email);
      return next;
    });
  };
  const toggleAll = () => {
    setPicked((prev) => (prev.size === contacts.length ? new Set() : new Set(contacts.map((c) => c.email))));
  };

  const send = () => {
    const selected = contacts.filter((c) => picked.has(c.email));
    setOpen(false);
    onSend(selected);
  };
  const sendCustom = () => {
    setOpen(false);
    onSend([]);   // mail client opens with empty To — user picks in Mail
  };

  const count = picked.size;
  const allChecked = count === contacts.length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-zinc-100 text-zinc-700 hover:bg-zinc-200 transition-colors"
          data-testid={triggerTestId}
          title="Pick one or more contacts and open a draft email"
        >
          <Mail className="w-3 h-3" />Email link
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid={`${listTestIdPrefix}-list`}>
        <div className="px-3 py-2 flex items-center justify-between border-b border-zinc-100">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
            Send to {count > 0 && <span className="ml-1 text-zinc-700 normal-case tracking-normal">({count} selected)</span>}
          </p>
          <button
            type="button"
            onClick={toggleAll}
            className="text-[11px] font-medium text-zinc-600 hover:text-zinc-900 underline"
            data-testid={`${listTestIdPrefix}-toggle-all`}
          >{allChecked ? "Clear all" : "Select all"}</button>
        </div>
        <div className="max-h-72 overflow-y-auto py-1">
          {contacts.map((c) => {
            const checked = picked.has(c.email);
            return (
              <label
                key={c.email}
                className="flex items-start gap-2.5 px-3 py-2 hover:bg-zinc-50 cursor-pointer transition-colors"
                data-testid={`${listTestIdPrefix}-item-${c.email}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => togglePick(c.email)}
                  className="mt-0.5 w-4 h-4 rounded border-zinc-300 text-zinc-950 focus:ring-zinc-400"
                  data-testid={`${listTestIdPrefix}-checkbox-${c.email}`}
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-zinc-900 truncate">{c.name}</span>
                  <span className="block text-[11px] text-zinc-500 truncate">{c.email}</span>
                </span>
              </label>
            );
          })}
        </div>
        <div className="border-t border-zinc-100 p-2 flex items-center justify-between gap-2 flex-wrap">
          <button
            type="button"
            onClick={sendCustom}
            className="text-[11px] text-zinc-600 hover:text-zinc-900 underline px-1"
            data-testid={`${listTestIdPrefix}-other`}
          >Other recipient…</button>
          <Btn
            type="button"
            size="sm"
            onClick={send}
            disabled={count === 0}
            data-testid={`${listTestIdPrefix}-confirm`}
          >
            <Send className="w-3 h-3" />
            {count === 0
              ? "Pick at least one"
              : count === 1
              ? "Open draft to 1"
              : `Open draft to ${count}`}
          </Btn>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default RecipientMultiSelectPopover;
